import { useEffect, useRef, useState } from 'react';
import { Search, X, BookOpen, Check } from 'lucide-react';
import { booksLibraryApi } from '../../services/adminApi';

// The search endpoint returns a mixed shape (DB rows expose an ASIN in `id` and
// the real id in `dbId`; discovery rows expose isbn13). Normalise every result
// to a single canonical identifier — isbn13 is our business key — plus clean
// display fields.
const normalizeBook = (b) => ({
  key: b._isbn13 || b.isbn13 || b.dbId || b.id,
  isbn13: b._isbn13 || b.isbn13 || null,
  title: b.title || '',
  author: Array.isArray(b.authors) ? b.authors.filter(Boolean).join(', ') : (b.author || ''),
  cover_url: b.thumbnail || b.cover_url || b.coverUrl || null,
  inCatalogue: b._source === 'database' || b.dbId != null
});

// Reusable book picker: type to search the catalogue and select a book, instead
// of pasting a raw numeric ID. Emits the selected book's id via onSelect.
const BookSearchSelect = ({
  label = 'Book',
  selectedId = '',
  selectedLabel = '',
  onSelect,
  onClear,
  required = false,
  disabled = false,
  helperText = 'Search by title or author.'
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(
    selectedId ? { id: selectedId, title: selectedLabel } : null
  );
  const boxRef = useRef(null);

  useEffect(() => {
    setPicked(selectedId ? { id: selectedId, title: selectedLabel } : null);
  }, [selectedId, selectedLabel]);

  // Debounced search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const { books } = await booksLibraryApi.searchBooks(trimmed);
        setResults((books || []).map(normalizeBook));
        setOpen(true);
      } catch (err) {
        setError(err.message || 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handlePick = (book) => {
    setPicked({ id: book.key, title: book.title, author: book.author, cover_url: book.cover_url });
    setQuery('');
    setResults([]);
    setOpen(false);
    setError(null);
    // Emit the canonical identifier (isbn13 when available) as `id`.
    onSelect?.({ id: book.key, isbn13: book.isbn13, title: book.title, author: book.author, cover_url: book.cover_url });
  };

  const handleClear = () => {
    setPicked(null);
    setQuery('');
    setResults([]);
    onClear?.();
    onSelect?.(null);
  };

  return (
    <div ref={boxRef}>
      <label className="block text-sm font-medium text-text-primary mb-2">
        {label}{required ? ' *' : ''}
      </label>

      {picked ? (
        <div className="flex items-center justify-between gap-3 px-3 py-2 border border-green-200 bg-green-50 rounded-lg">
          <div className="flex items-center gap-3 min-w-0">
            {picked.cover_url ? (
              <img src={picked.cover_url} alt={picked.title} className="w-8 h-11 object-cover rounded" />
            ) : (
              <div className="w-8 h-11 rounded bg-gray-100 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-gray-400" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-text-primary truncate flex items-center gap-1">
                <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                {picked.title || `Book #${picked.id}`}
              </div>
              {picked.author && <div className="text-xs text-text-secondary truncate">{picked.author}</div>}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded"
              title="Change book"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length && setOpen(true)}
            placeholder="Search books by title or author..."
            disabled={disabled}
            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-50"
          />

          {open && (loading || error || results.length > 0 || query.trim().length >= 3) && (
            <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
              {loading && <div className="px-3 py-2 text-sm text-text-secondary">Searching…</div>}
              {error && <div className="px-3 py-2 text-sm text-red-600">{error}</div>}
              {!loading && !error && results.length === 0 && (
                <div className="px-3 py-2 text-sm text-text-secondary">No books found.</div>
              )}
              {results.map((book) => (
                <button
                  key={book.key}
                  type="button"
                  onClick={() => handlePick(book)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                >
                  {book.cover_url ? (
                    <img src={book.cover_url} alt={book.title} className="w-8 h-11 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-11 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-text-primary truncate">{book.title || 'Untitled'}</div>
                    <div className="text-xs text-text-secondary truncate">{book.author || 'Unknown author'}</div>
                  </div>
                  {!book.inCatalogue && (
                    <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 flex-shrink-0">
                      not in library
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {helperText && <p className="text-xs text-text-secondary mt-1">{helperText}</p>}
    </div>
  );
};

export default BookSearchSelect;
